/**
 * Tax calculation and cart total utility functions.
 *
 * Extracted from Core/index.js — pure helpers with no React or state dependencies.
 */
import { v4 as uuidv4 } from "uuid";
import { logAdjustOrderTotals } from "../../../../utils/dataIntegrity";
import { roundTo } from "../../../../utils/tillHelpers";

/**
 * Build a tax map from cart items, aggregating taxAmount and nettotal by taxRate.
 * Handles both regular items and custom-mix items with children (where parent has no taxRate).
 *
 * @param {Array} items - Cart items array
 * @param {number} precision - Decimal precision for rounding
 * @returns {{ taxMap: Object, taxDetails: Array, combinedTaxRates: Array }}
 */
export function buildTaxMap(items, precision) {
  const taxMap = {};
  const taxDetails = [];

  for (const item of items) {
    if (!item.isGiftCard) {
      if (item.taxRate >= 0 && item.tax) {
        const taxRate = item.taxRate;
        const taxAmount = parseFloat(parseFloat(item.taxAmount)?.toFixed(precision));
        const nettotal = parseFloat(parseFloat(item.nettotal - item.taxAmount)?.toFixed(precision));
        if (taxMap[taxRate]) {
          taxMap[taxRate].taxAmount += taxAmount;
          taxMap[taxRate].nettotal += nettotal;
        } else {
          taxMap[taxRate] = {
            taxAmount,
            nettotal,
            tax: item.tax,
            taxRate,
            taxCategory: item.taxCategory,
            taxName: item.taxName,
          };
        }
      }

      // Handle custom mix items (no taxRate on parent, children carry tax info)
      if (!item.taxRate && item.children?.length > 0) {
        for (const child of item.children) {
          if (child.taxRate && child.tax) {
            const taxRate = child.taxRate;
            const taxAmount = parseFloat(parseFloat(child.taxAmount)?.toFixed(precision));
            const nettotal = parseFloat(parseFloat(child.nettotal - child.taxAmount)?.toFixed(precision));
            if (taxMap[taxRate]) {
              taxMap[taxRate].taxAmount += taxAmount;
              taxMap[taxRate].nettotal += nettotal;
            } else {
              taxMap[taxRate] = {
                taxAmount,
                nettotal,
                tax: child.tax,
                taxRate,
                taxCategory: child.taxCategory,
                taxName: child.taxName,
              };
            }
          }
        }
      }
    }
  }

  const combinedTaxRates = Object.keys(taxMap).map((taxRate) => {
    const entry = taxMap[taxRate];
    taxDetails.push({
      csTaxID: entry.tax,
      taxableAmt: entry.nettotal,
      taxAmt: entry.taxAmount,
      taxRate,
      taxCategory: entry.taxCategory,
      taxName: entry.taxName,
    });
    return { csTaxID: entry.tax, taxableAmt: entry.nettotal, taxAmt: entry.taxAmount };
  });

  return { taxMap, taxDetails, combinedTaxRates };
}

/**
 * Group items by tax rate, then compute per-group totals including SGST/CGST split.
 * Flattens children into the item pool before grouping.
 *
 * @param {Array} items - Cart items (may include children)
 * @param {number} precision - Decimal precision
 * @param {Object} [options]
 * @param {string} [options.taxField="taxAmount"] - Field name for tax value on each item
 * @param {string} [options.netField="nettotal"] - Field name for net total on each item
 * @param {boolean} [options.filterCanceled=false] - Whether to filter out canceled items
 * @param {boolean} [options.isInterState=false] - When true, returns IGST instead of SGST/CGST
 * @returns {Array} Array of { taxRate, totalLinenet, totalLinetax, totalLinegross, totalSgst, totalCgst, totalIgst }
 */
export function groupItemsByTaxRate(items, precision, options = {}) {
  const { taxField = "taxAmount", netField = "nettotal", filterCanceled = false, isInterState = false } = options;

  let allItems = items.flatMap((item) => (Array.isArray(item.children) && item.children.length > 0 ? item.children : [item]));

  if (filterCanceled) {
    allItems = allItems.filter((i) => i.isCanceled !== "Y");
  }

  const groupedItems = allItems.reduce((acc, item) => {
    const key = item.tax?.rate ?? item.taxRate ?? "0";
    acc[key] = acc[key] || [];
    acc[key].push(item);
    return acc;
  }, {});

  return Object.entries(groupedItems).map(([taxRate, groupItems]) => {
    const totalTaxAmount = groupItems.reduce((sum, item) => {
      const taxValue = (item.tax?.rate !== undefined && item.tax?.rate !== null ? item[taxField] : item.taxAmount) || 0;
      return sum + parseFloat(parseFloat(taxValue)?.toFixed(precision));
    }, 0);

    const totalNetTotal = groupItems.reduce((sum, item) => {
      const netValue = (item.tax?.rate !== undefined && item.tax?.rate !== null ? item[netField] : item.nettotal) || 0;
      return sum + parseFloat(netValue);
    }, 0);

    const totalSgstAmount = groupItems.reduce((sum, item) => {
      const sgstValue = item.sgst || item.tax?.sgst || parseFloat(item[taxField] || item.taxAmount || 0) / 2;
      return sum + parseFloat(parseFloat(sgstValue)?.toFixed(precision));
    }, 0);

    const totalCgstAmount = groupItems.reduce((sum, item) => {
      const cgstValue = item.cgst || item.tax?.cgst || parseFloat(item[taxField] || item.taxAmount || 0) / 2;
      return sum + parseFloat(parseFloat(cgstValue)?.toFixed(precision));
    }, 0);

    return {
      taxRate,
      totalLinenet: parseFloat(parseFloat(totalNetTotal - totalTaxAmount)?.toFixed(precision)),
      totalLinetax: parseFloat(parseFloat(totalTaxAmount)?.toFixed(precision)),
      totalLinegross: parseFloat(parseFloat(totalNetTotal)?.toFixed(precision)),
      totalSgst: isInterState ? 0 : parseFloat(parseFloat(totalSgstAmount)?.toFixed(precision)),
      totalCgst: isInterState ? 0 : parseFloat(parseFloat(totalCgstAmount)?.toFixed(precision)),
      totalIgst: isInterState ? parseFloat(parseFloat(totalTaxAmount)?.toFixed(precision)) : 0,
    };
  });
}

/**
 * Compute line-level accounting fields for a single cart item.
 * Produces: linetax, linenet, linestd, linegross, netunit, listprice,
 *           grossunit, grossstd, grosslist, netList, unitPrice, taxAmount.
 *
 * @param {Object} item - Cart line item (mutated in place for backward compat)
 * @param {string} taxIncludeFlag - "Y" if prices are tax-inclusive
 * @param {number} precision - Decimal precision
 * @returns {Object} The enriched fields (same reference as `item`)
 */
export function enrichLineItemFields(item, taxIncludeFlag, precision) {
  if (item.isGiftCard) return item;

  const taxRate = Number(item.taxRate) || 0;
  const listPrice = Number(item.listPrice) || 0;
  const sunitprice = Number(item.sunitprice) || 0;
  const weight = Number(item.weight) || 1;
  const nettotalFixed = parseFloat(parseFloat(item.nettotal)?.toFixed(precision));

  const lineTax = listPrice * (1 + taxRate / 100);
  const stdTax = sunitprice * (1 + taxRate / 100);

  const unitPrice =
    isFinite(item.nettotal / weight) && weight !== 0
      ? taxIncludeFlag === "Y"
        ? item.nettotal / weight / (1 + taxRate / 100)
        : Number(item.salePrice) || 0
      : 0;

  const gross_list = taxIncludeFlag === "Y" ? item.listPrice : lineTax;
  const netList =
    taxIncludeFlag === "Y"
      ? item.listPrice - parseFloat(parseFloat(item.listPrice - item.listPrice / (1 + item.taxRate / 100))?.toFixed(precision))
      : item.listPrice;
  const grossStd = taxIncludeFlag === "Y" ? item.sunitprice : stdTax;

  Object.assign(item, {
    linetax: Math.abs(nettotalFixed) > 0 ? parseFloat(parseFloat(item.taxAmount)?.toFixed(precision)) : 0,
    linenet: Math.abs(nettotalFixed) > 0 ? parseFloat(parseFloat(item.nettotal - item.taxAmount)?.toFixed(precision)) : 0,
    linestd: Math.abs(nettotalFixed) > 0 ? parseFloat(parseFloat(grossStd * item.weight)?.toFixed(precision)) : 0,
    linegross: Math.abs(nettotalFixed) > 0 ? item.nettotal : 0,
    netunit: Math.abs(nettotalFixed) > 0 ? parseFloat(parseFloat(unitPrice)?.toFixed(precision)) : 0,
    listprice: Math.abs(nettotalFixed) > 0 ? item.listPrice : 0,
    grossunit: Math.abs(nettotalFixed) > 0 ? parseFloat(parseFloat(unitPrice)?.toFixed(precision)) * (item.isReturn ? -1 : 1) : 0,
    grossstd: Math.abs(nettotalFixed) > 0 ? roundTo(grossStd, precision) : 0,
    grosslist: Math.abs(nettotalFixed) > 0 ? gross_list : 0,
    netList: Math.abs(nettotalFixed) > 0 ? netList : 0,
    unitPrice: Math.abs(nettotalFixed) > 0 ? unitPrice : 0,
    taxAmount: Math.abs(nettotalFixed) > 0 ? parseFloat(parseFloat(item.taxAmount)?.toFixed(precision)) : 0,
  });

  return item;
}

/**
 * Split a cart line (weight/nettotal/taxAmount/discount schema) into two independent
 * lines by quantity: a remainder line that stays active, and a cancelled fragment.
 * Money fields are split by ratio, with the remainder computed as original-minus-cancelled
 * (not re-multiplied) so the two fragments always sum back exactly to the original — no
 * rounding drift from double rounding.
 *
 * @param {Object} line - Source cart item (not mutated — both returned lines are copies)
 * @param {number} cancelQty - Quantity to move into the cancelled fragment (must be < line.weight)
 * @param {string} taxIncludeFlag - "Y" if prices are tax-inclusive
 * @param {number} precision - Decimal precision
 * @param {Object} [ids] - Optional pre-generated identity for the cancelled fragment, so callers
 *                          that need the same fragment id across multiple mirrored item arrays
 *                          (e.g. cart.items and wsData.items) can keep them in sync.
 * @param {string} [ids.newLineId]
 * @param {string} [ids.newFbOrderLineId]
 * @returns {{ remainderLine: Object, cancelledFragment: Object }}
 */
export function splitLineByQuantity(line, cancelQty, taxIncludeFlag, precision, ids = {}) {
  const totalQty = parseFloat(line.weight);
  const keepQty = totalQty - cancelQty;
  const ratio = totalQty > 0 ? cancelQty / totalQty : 0;

  const cancelledFragment = { ...line };
  const remainderLine = { ...line };

  ["nettotal", "taxAmount", "discount"].forEach((field) => {
    const total = parseFloat(line[field] || 0);
    const cancelledPart = roundTo(total * ratio, precision);
    cancelledFragment[field] = cancelledPart;
    remainderLine[field] = roundTo(total - cancelledPart, precision);
  });

  cancelledFragment.weight = cancelQty;
  cancelledFragment.lineId = ids.newLineId || uuidv4().replace(/-/g, "").toUpperCase();
  cancelledFragment.fbOrderLineId = ids.newFbOrderLineId || uuidv4().replace(/-/g, "").toUpperCase();
  cancelledFragment.isCanceled = "Y";
  cancelledFragment.orderStatus = "CL";
  cancelledFragment.firedQty = cancelQty;
  cancelledFragment.sendQty = 0;

  remainderLine.weight = keepQty;
  remainderLine.firedQty = keepQty;
  remainderLine.sendQty = 0;

  enrichLineItemFields(cancelledFragment, taxIncludeFlag, precision);
  enrichLineItemFields(remainderLine, taxIncludeFlag, precision);

  return { remainderLine, cancelledFragment };
}

/**
 * Sum gross and tax totals from an array of line items.
 *
 * @param {Array} lines
 * @returns {{ totalLineGross: number, totalLineTax: number }}
 */
export function sumLineTotals(lines) {
  let totalLineGross = 0;
  let totalLineTax = 0;

  lines.forEach((line) => {
    totalLineGross += line.nettotal;
    totalLineTax += line.taxAmount;
  });

  return { totalLineGross, totalLineTax };
}

/**
 * Compare line-level totals against order-level totals and adjust if the
 * difference exceeds a 0.01 tolerance (rounding drift).
 *
 * @param {Object} order - Object with `total` and `tax` properties
 * @param {Array} lines - Line items
 * @returns {Object} A new order object with adjusted total/tax
 */
export function adjustOrderTotals(order, lines) {
  const { totalLineGross, totalLineTax } = sumLineTotals(lines);
  let adjustedOrderGross = order.total;
  let adjustedOrderTax = order.tax;

  const grossDifference = totalLineGross - adjustedOrderGross;
  const taxDifference = totalLineTax - adjustedOrderTax;

  if (Math.abs(grossDifference) > 0.01) {
    adjustedOrderGross += grossDifference;
  }

  if (Math.abs(taxDifference) > 0.01) {
    adjustedOrderTax += taxDifference;
  }

  // DI-07: Log any silent adjustments for audit trail
  logAdjustOrderTotals(
    { total: order.total, tax: order.tax },
    { total: adjustedOrderGross, tax: adjustedOrderTax }
  );

  return {
    ...order,
    total: adjustedOrderGross,
    tax: adjustedOrderTax,
  };
}

/**
 * Accumulate cart-level totals (total, tax, discount, qty) from line items.
 * Skips items with isCanceled === "Y".
 *
 * @param {Array} items - Cart items
 * @param {number} precision - Decimal precision
 * @returns {{ totalPrice: number, totalTax: number, totalDiscounts: number, totalItemsQty: number }}
 */
export function accumulateCartTotals(items, precision) {
  let totalPrice = 0;
  let totalTax = 0;
  let totalDiscounts = 0;
  let totalItemsQty = 0;

  for (let i = 0; i < items.length; i += 1) {
    if (items[i].isCanceled === "Y") continue;
    totalPrice += items[i].nettotal;
    totalItemsQty += items[i].weight;
    totalTax += items[i].taxAmount;
    totalDiscounts += items[i].discount;
  }

  return { totalPrice, totalTax, totalDiscounts, totalItemsQty };
}

/**
 * Build an item summary object with decimal/non-decimal breakdowns.
 *
 * @param {Array} items - Cart items (may have children)
 * @param {number} precision - Decimal precision
 * @returns {{ decimalItems: { totalQty: number, totalAmount: number }, nonDecimalItems: { totalQty: number, totalAmount: number }, itemsQty: number, items: number }}
 */
export function buildItemSummary(items, precision) {
  const summary = {
    decimalItems: { totalQty: 0, totalAmount: 0 },
    nonDecimalItems: { totalQty: 0, totalAmount: 0 },
    itemsQty: 0,
    items: 0,
  };

  for (const item of items) {
    if (Array.isArray(item.children) && item.children.length > 0) {
      item.children.forEach((child) => {
        const isDecimal = child.uomData?.[0]?.decimal === "Y" || child.isDecimal;
        const summaryKey = isDecimal ? "decimalItems" : "nonDecimalItems";

        summary.itemsQty += child.weight;
        summary.items += 1;
        summary[summaryKey].totalQty += child.weight;
        summary[summaryKey].totalAmount += parseFloat(parseFloat(child.nettotal)?.toFixed(precision));
      });
    } else {
      const isDecimal = item.uomData?.[0]?.decimal === "Y" || item.isDecimal;
      const summaryKey = isDecimal ? "decimalItems" : "nonDecimalItems";

      summary.itemsQty += item.weight;
      summary.items += 1;
      summary[summaryKey].totalQty += item.weight;
      summary[summaryKey].totalAmount += parseFloat(parseFloat(item.nettotal)?.toFixed(precision));
    }
  }

  return summary;
}
